/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Set creative direction", description = "Save visual styles and approve copy references to guide the images and copy agents generate in Ad Studio and Lifecycle Studio, and tag ad creative to compare performance.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Set creative direction",
        description: "Save visual styles and approve copy references to guide the images and copy agents generate in Ad Studio and Lifecycle Studio, and tag ad creative to compare performance.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Save visual styles",
          "depth": 0,
          "id": "save-visual-styles"
        }, {
          "value": "Create a style",
          "depth": 1,
          "id": "create-a-style"
        }, {
          "value": "Set where a style applies",
          "depth": 1,
          "id": "set-where-a-style-applies"
        }, {
          "value": "Test and publish a style",
          "depth": 1,
          "id": "test-and-publish-a-style"
        }, {
          "value": "Add copy references",
          "depth": 0,
          "id": "add-copy-references"
        }, {
          "value": "Connect a source",
          "depth": 1,
          "id": "connect-a-source"
        }, {
          "value": "Review and approve references",
          "depth": 1,
          "id": "review-and-approve-references"
        }, {
          "value": "Set brand access",
          "depth": 1,
          "id": "set-brand-access"
        }, {
          "value": "Tag your ad creative",
          "depth": 0,
          "id": "tag-your-ad-creative"
        }, {
          "value": "Run tagging",
          "depth": 1,
          "id": "run-tagging"
        }, {
          "value": "Create a tag",
          "depth": 1,
          "id": "create-a-tag"
        }, {
          "value": "Adjust a built-in tag",
          "depth": 1,
          "id": "adjust-a-built-in-tag"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3",
      code: "code",
      ol: "ol"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketing teams, creative teams, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["The ", _jsxs(_components.a, {
                    href: "/workspace-management/roles#agents",
                    children: [_jsx(_components.strong, {
                      children: "Configure Agents"
                    }), " grant"]
                  }), "."]
                }), _jsx("li", {
                  children: "Ad Studio or Lifecycle Studio."
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Create reusable visual styles and approve copy references to guide generated images and copy."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Use styles and copy references to guide the creative content agents generate in Ad Studio and Lifecycle Studio. You manage them in ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge"
        }), "."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Styles"
          }), " describe how generated images should look, including lighting, composition, and color palette."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Copy references"
          }), " provide approved examples of emails, SMS messages, push notifications, and ads that agents can use when writing new content."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Tags"
          }), " label your ad creative by attributes such as hook tactic or offer type, so you can compare performance by creative approach in Ad Studio."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Styles and copy references complement your ", _jsx(_components.a, {
          href: "/context-hub/brands",
          children: "brand profiles"
        }), ", which define logos, colors, and fonts. Use ", _jsx(_components.a, {
          href: "/context-hub/guardrails",
          children: "guardrails"
        }), " to control how agents can modify images."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "save-visual-styles",
        children: "Save visual styles"
      }), "\n", _jsx(_components.p, {
        children: "Create a style when you want generated images to share the same look. Write the art direction once instead of repeating it in every prompt. For example, you might keep one style for warm, golden-hour lifestyle ads and another for clean editorial email imagery."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-knowledge-styles-gallery.png",
          alt: "Styles gallery in Knowledge with the Create style menu"
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-a-style",
        children: "Create a style"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Styles"
        }), " and select ", _jsx(_components.strong, {
          children: "Create style"
        }), ". Choose how to start:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create with Context Agent"
          }), " — Describe the look or share reference images, and the agent drafts the style with you."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Start from a template"
          }), " — Fill in prompts for layout, photography, lighting, and palette."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create from scratch"
          }), " — Write the style yourself."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-creative-create-style-menu.png",
          alt: "Create style menu with Create with Context Agent, Start from a template, and Create from scratch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["A style is a single Markdown document. Describe the art direction in concrete terms: \"Soft, diffused studio light\" gives an agent more to work with than \"premium feel.\" Include what to avoid, and add reference images from your own creative under the parts of the look they show. Type ", _jsx(_components.code, {
          children: "/"
        }), " in the document to insert a reference image, or select ", _jsx(_components.strong, {
          children: "Upload .md file"
        }), " to import an existing style."]
      }), "\n", _jsx(_components.h3, {
        id: "set-where-a-style-applies",
        children: "Set where a style applies"
      }), "\n", _jsx(_components.p, {
        children: "Limit a style to the brands and products it's meant for, so it doesn't shape content it wasn't written for."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Brand profiles"
          }), ", select a brand when the look belongs to one brand. Leave it on ", _jsx(_components.strong, {
            children: "All brand profiles"
          }), " for a look you use everywhere."]
        }), "\n", _jsxs(_components.li, {
          children: ["To limit the style to ads or to lifecycle messages, select ", _jsx(_components.strong, {
            children: "Ad Studio"
          }), " or ", _jsx(_components.strong, {
            children: "Lifecycle Studio"
          }), " in ", _jsx(_components.strong, {
            children: "Agents with access"
          }), ". This setting appears when your workspace has both products. See ", _jsx(_components.a, {
            href: "/context-hub/business-context#choose-where-agents-use-context",
            children: "Choose where agents use context"
          }), " for what each option means."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Usage guidance"
          }), ", say when agents should use the style, such as \"Use for product launch ads on Meta and Instagram. Not for promos or sales.\" Agents use this guidance to match the style to requests."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-creative-style-settings.png",
          alt: "New style with Brand profiles, Agents with access set to Ad Studio, Usage guidance, and the Content template"
        })
      }), "\n", _jsx(_components.h3, {
        id: "test-and-publish-a-style",
        children: "Test and publish a style"
      }), "\n", _jsxs(_components.p, {
        children: ["Agents use a style only after you turn on ", _jsx(_components.strong, {
          children: "Published"
        }), ". Until then, it shows as ", _jsx(_components.strong, {
          children: "Draft"
        }), " in the gallery."]
      }), "\n", _jsx(_components.p, {
        children: "Before you publish, test the style from its actions menu, on its card or its detail page:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Test style in Ad Studio"
          }), " — Opens Ad Studio with the style applied, for styles that Ad Studio can use."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Test style in Agents"
          }), " — Opens a new Agents chat with the style applied, for styles that Lifecycle Studio can use. Appears only in workspaces with Agents."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To take a style out of use without deleting it, select ", _jsx(_components.strong, {
          children: "Unpublish"
        }), " from its actions menu in the styles list, or turn off ", _jsx(_components.strong, {
          children: "Published"
        }), " on its detail page."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-creative-style-actions-menu.png",
          alt: "Style detail page with Published turned on and the actions menu open to Test style in Ad Studio"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "add-copy-references",
        children: "Add copy references"
      }), "\n", _jsx(_components.p, {
        children: "Add copy references when you want generated copy to sound like the messages your team already sends. Agents use the examples you approve as a reference for tone, structure, and calls to action."
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Copy references"
        }), ". You connect and approve each channel separately: ", _jsx(_components.strong, {
          children: "Email"
        }), ", ", _jsx(_components.strong, {
          children: "SMS"
        }), ", ", _jsx(_components.strong, {
          children: "Push"
        }), ", and ", _jsx(_components.strong, {
          children: "Ads"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-knowledge-copy-references.png",
          alt: "Copy references list with Email, SMS, and Push ready to review and Ads not connected"
        })
      }), "\n", _jsx(_components.h3, {
        id: "connect-a-source",
        children: "Connect a source"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Connect"
            }), " on the channel you want to add."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Choose where the examples come from:"
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "ESP"
              }), " — Read past sends from your email service provider."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Warehouse"
              }), " — Read sample copy from a table in a connected warehouse source."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Paste or upload"
              }), " — Paste examples, or upload a Markdown or CSV file."]
            }), "\n"]
          }), "\n", _jsxs(_components.p, {
            children: ["For ", _jsx(_components.strong, {
              children: "Ads"
            }), ", search the Meta Ad Library instead and select your brand's advertiser page."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["For email, SMS, and push, select ", _jsx(_components.strong, {
              children: "Continue"
            }), " and decide which sends to use: set how many references to extract and, for ESP and warehouse sources, a date range."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Save & connect"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-creative-ads-copy-references-dialog.png",
          alt: "Add copy references dialog for Ads with a Meta Ad Library brand page search and Save & connect"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["An agent pulls the examples and prepares them for review. This can take a few minutes. When it finishes, the channel shows ", _jsx(_components.strong, {
          children: "Ready to review"
        }), ". If the channel shows ", _jsx(_components.strong, {
          children: "Needs retry"
        }), ", select ", _jsx(_components.strong, {
          children: "Retry"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "review-and-approve-references",
        children: "Review and approve references"
      }), "\n", _jsx(_components.p, {
        children: "Agents don't use copy references until you approve them."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Review"
          }), " on the channel."]
        }), "\n", _jsxs(_components.li, {
          children: ["Check the proposed examples. Select an example to edit its fields, turn off ", _jsx(_components.strong, {
            children: "Used as a reference"
          }), " to exclude it, or select ", _jsx(_components.strong, {
            children: "Remove"
          }), " to drop it."]
        }), "\n", _jsxs(_components.li, {
          children: ["To include a message the agent didn't pull, select ", _jsx(_components.strong, {
            children: "Add example"
          }), " and enter its copy."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Approve"
          }), " to make the references live, or ", _jsx(_components.strong, {
            children: "Reject"
          }), " to discard the whole set."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-creative-email-references-review.png",
          alt: "Email references ready to review, with Add example and the Reject and Approve buttons"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-creative-email-reference-detail.png",
          alt: "Email reference open for editing, with Subject, Preheader, Body, CTA, Used as a reference, and Remove"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["After you approve, the channel shows ", _jsx(_components.strong, {
          children: "Live"
        }), ". You can keep refining a live set: add references, include or exclude examples, or select ", _jsx(_components.strong, {
          children: "Refresh"
        }), " to pull a fresh set from the source. Refresh isn't available for examples you pasted or uploaded. A refresh comes back for review before it replaces what agents use."]
      }), "\n", _jsx(_components.h3, {
        id: "set-brand-access",
        children: "Set brand access"
      }), "\n", _jsxs(_components.p, {
        children: ["Open a live set to set its ", _jsx(_components.strong, {
          children: "Brand profiles"
        }), ". Assign it to a brand when that brand writes differently from the rest, or leave it on ", _jsx(_components.strong, {
          children: "All brand profiles"
        }), " to share one voice across brands. Use ", _jsx(_components.strong, {
          children: "Usage guidance"
        }), " to tell agents when the references apply."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "tag-your-ad-creative",
        children: "Tag your ad creative"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Creative tags"
      }), "\n", _jsx(_components.p, {
        children: "Tag your ad creative when you want to compare performance by creative approach instead of ad by ad. For example, you can see whether ads that open with a question outperform ads that open with a product shot. An agent labels the images and videos from your connected ad accounts. You decide which tags exist and which values they can take."
      }), "\n", _jsxs(_components.p, {
        children: ["After tagging runs, you can use the tags in ", _jsx(_components.a, {
          href: "/ad-studio/insights",
          children: "Insights"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Group ad performance by a tag. In ", _jsx(_components.strong, {
            children: "Group by"
          }), ", select ", _jsx(_components.strong, {
            children: "Creative tags"
          }), " and then the tag."]
        }), "\n", _jsx(_components.li, {
          children: "Open an ad to check its tags. You can correct a list tag's value, and later runs keep the values you set by hand."
        }), "\n", _jsx(_components.li, {
          children: "Ask an agent in Insights which creative patterns perform best. The agent can compare performance across your tags."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Tags"
        }), " to manage tags. Hightouch provides built-in tags, such as ", _jsx(_components.strong, {
          children: "Hook tactic"
        }), ", ", _jsx(_components.strong, {
          children: "Messaging angle"
        }), ", ", _jsx(_components.strong, {
          children: "Offer type"
        }), ", and ", _jsx(_components.strong, {
          children: "CTA style"
        }), ", which show as ", _jsx(_components.strong, {
          children: "System"
        }), ". Tags you create show as ", _jsx(_components.strong, {
          children: "Custom"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-knowledge-tags.png",
          alt: "Tags list in Knowledge with built-in System tags and the Run tagging and Create tag buttons"
        })
      }), "\n", _jsx(_components.h3, {
        id: "run-tagging",
        children: "Run tagging"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Run tagging"
        }), " on the ", _jsx(_components.strong, {
          children: "Tags"
        }), " page. A run tags creative that hasn't been tagged yet, and re-tags existing creative for any tag you've changed since the last run. It also tags the competitor ads you track in Insights. The button shows ", _jsx(_components.strong, {
          children: "Tagging…"
        }), " until the run finishes."]
      }), "\n", _jsx(_components.p, {
        children: "Run tagging again after you create or change a tag, so the new definition applies to the creative you already have."
      }), "\n", _jsx(_components.h3, {
        id: "create-a-tag",
        children: "Create a tag"
      }), "\n", _jsx(_components.p, {
        children: "Create a tag when the built-in tags don't cover an attribute you want to compare, such as a recurring creative concept or a sub-brand."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to ", _jsx(_components.strong, {
              children: "Context Hub > Knowledge > Tags"
            }), " and select ", _jsx(_components.strong, {
              children: "Create tag"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter a name and a ", _jsx(_components.strong, {
              children: "Key"
            }), ". The key names the tag in your warehouse tables, and you can't change it after you create the tag."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In ", _jsx(_components.strong, {
              children: "Usage guidance"
            }), ", tell the agent how to recognize the tag. For example: \"Whether the ad features our mascot, Max the fox, and how prominently.\""]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Choose a ", _jsx(_components.strong, {
              children: "Value type"
            }), ":"]
          }), "\n", _jsxs(_components.table, {
            children: [_jsx(_components.thead, {
              children: _jsxs(_components.tr, {
                children: [_jsx(_components.th, {
                  children: "Value type"
                }), _jsx(_components.th, {
                  children: "Use when"
                })]
              })
            }), _jsxs(_components.tbody, {
              children: [_jsxs(_components.tr, {
                children: [_jsx(_components.td, {
                  children: _jsx(_components.strong, {
                    children: "List"
                  })
                }), _jsxs(_components.td, {
                  children: ["You want to compare creative across a fixed set of values. Enter them in ", _jsx(_components.strong, {
                    children: "Allowed values"
                  }), "."]
                })]
              }), _jsxs(_components.tr, {
                children: [_jsx(_components.td, {
                  children: _jsx(_components.strong, {
                    children: "Text"
                  })
                }), _jsx(_components.td, {
                  children: "You want a short description that doesn't fit a fixed set of values."
                })]
              }), _jsxs(_components.tr, {
                children: [_jsx(_components.td, {
                  children: _jsx(_components.strong, {
                    children: "Number"
                  })
                }), _jsx(_components.td, {
                  children: "The attribute is a count or measurement, such as the number of products shown."
                })]
              })]
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In ", _jsx(_components.strong, {
              children: "Applies to"
            }), ", select ", _jsx(_components.strong, {
              children: "Images"
            }), ", ", _jsx(_components.strong, {
              children: "Videos"
            }), ", or both."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Create tag"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-knowledge-tag-value-type.png",
          alt: "Value type options for a tag: List, Text, and Number"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Turn on ", _jsx(_components.strong, {
          children: "Multiple values"
        }), " when one creative can have more than one value, such as an ad that features several products. Turn off ", _jsx(_components.strong, {
          children: "Require values"
        }), " when some creative won't have a value for the tag."]
      }), "\n", _jsx(_components.h3, {
        id: "adjust-a-built-in-tag",
        children: "Adjust a built-in tag"
      }), "\n", _jsxs(_components.p, {
        children: ["Edit a system tag when its values or usage guidance don't match how your team talks about creative. For example, you might add an offer type your brand runs often. Saving any change makes the tag your workspace's version: it shows as ", _jsx(_components.strong, {
          children: "Overridden"
        }), ", and future updates to the built-in tag no longer apply."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-knowledge-tag-system-detail.png",
          alt: "Hook tactic system tag with the notice that saving changes overrides the built-in tag for this workspace"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To return to the built-in definition, open the tag's actions menu and select ", _jsx(_components.strong, {
          children: "Reset to default"
        }), ". You can delete custom tags but not system tags. Resetting or deleting a tag keeps the values already written to your warehouse."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/guardrails",
            children: "Set guardrails"
          }), " to control how agents can modify images in ads and emails."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/assets",
            children: "Manage assets"
          }), " to add the images, templates, and product catalogs agents use to generate content."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
